import { FC, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import Header from '../../../common/Header/Header';
import Button from '../../../components/Button/Button';
import {
  ButtonsSeparator,
  Page,
  TableWrapper,
} from '../../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Table from '../../../components/Table/Table';
import { useDeleteRecipeRecords, useDeleteRecipes } from '../../../services/recipe/recipe.service';

import { Variant } from '../../../components/Button/Button.types';
import { Buttons } from '../../OptionRecipes/AddOption/AddOption.styles';
import { ColDef, ITooltipParams, SelectionChangedEvent } from 'ag-grid-community';
import { showToast } from '../../../components/Toast/Toast';
import DeleteConfirmationModal from '../../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { ToastTypes } from '../../../components/Toast/Toast.types';

const DeleteRecipes: FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { data, isError: recipesFectchError, refetch } = useDeleteRecipes();
  const [selectedRecipes, setSelectedRecipes] = useState<any[]>([]);
  useFetchErrorToast([
    { isError: recipesFectchError, label: 'Error fetching unused Recipes', id: 'DeleteRecipesListFetchError' },
  ]);
  const { mutate: deleteRecipeRecords } = useDeleteRecipeRecords();
  const onSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedRecipes(event.api.getSelectedRows());
  };
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const onRemoveRow = () => {
    const recipesToDelete: any[] = selectedRecipes.map((item) => item.specNo);
    deleteRecipeRecords(
      { specNo: recipesToDelete },
      {
        onSuccess: () => {
          showToast({
            label: 'Recipes Deleted Successfully!',
            id: 'recipesDeleteSuccess',
            toastType: ToastTypes.Success
          });
          refetch();
          setSelectedRecipes([]);
          closeDeleteModal();
        },
        onError: (e) => {
          showToast(genericErrorShowToastOptions);
          closeDeleteModal();
        },
      }
    );

  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const onFirstDataRendered = () => {
    const inputElement = document.querySelector(
      'input.ag-input-field-input.ag-text-field-input'
    ) as HTMLInputElement;
    inputElement?.focus();
  };

  const columnDef = [
    {
      minWidth: 48,
      field: '',
      maxWidth: 70,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      floatingFilter: false,
      sortable: false,
    },
    {
      field: 'specNo',
      headerName: 'Spec'
    },
    {
      flex: 2,
      field: 'specDescription',
      headerName: 'Spec Description',
      tooltipValueGetter: (params: ITooltipParams) => params.value,
      cellRenderer: 'agGroupCellRenderer',
      filter: 'agTextColumnFilter',
    },
    {
      flex: 1,
      field: 'classNo',
      headerName: 'Class',
      valueGetter: (params: any) => {
        const {classNo, classDescription} = params.data;
        return `${classNo} ~ ${classDescription}`;
      },
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      flex: 1,
      field: 'baseNo',
      headerName: 'Base',
      valueGetter: (params: any) => {
        const {baseNo, baseDescription} = params.data;
        return `${baseNo} ~ ${baseDescription}`;
      },
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      flex: 1,
      field: 'sizeNo',
      headerName: 'Size',
      valueGetter: (params: any) => {
        const {sizeNo, sizeDescription} = params.data;
        return `${sizeNo} ~ ${sizeDescription}`;
      },
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      flex: 1,
      field: 'productNo',
      headerName: 'Product',
      valueGetter: (params: any) => {
        const {productNo, productDescription} = params.data;
        return `${productNo} ~ ${productDescription}`;
      },
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
  ];

  const filterForConfirmationDisplay = (value: ColDef) => {
    return value.field !== '';
  }

  const colDefsForConfirmationDisplay = (value: ColDef) => {
    return {...value, floatingFilter: false, filter: undefined, cellRenderer: undefined};
  }

  const previewColDefs = columnDef.map(colDefsForConfirmationDisplay).filter(filterForConfirmationDisplay);

  return (
    <>
      <Header
        breadcrumb={{ to: '/recipes', label: 'Back to Recipes' }}
        title='Delete Recipes'
        titleTestId={'DeleteRecipesPageTitle'}
      />
      <Page>
        <TableWrapper>
          <Table
            onFirstDataRendered={onFirstDataRendered}
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            width={'99%'}
            onSelectionChanged={onSelectionChanged}
            groupTotalRow={'bottom'}
            suppressRowClickSelection={false}
            rowMultiSelectWithClick={true}
            columnDefs={columnDef}
            rowSelection='multiple'
            isSizeColumnsToFit={false}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Delete'
            variant={Variant.Primary}
            testId='DeleteRecipeButton'
            onClick={openDeleteModal}
            disabled={selectedRecipes.length === 0}
          />
          <ButtonsSeparator />
        </Buttons>
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={onRemoveRow}
          cancelBtnTestId='cancelDeleteRecipesModal'
          confirmBtnTestId='confirmDeleteRecipesModal'
          title={'Delete Recipes'}
          message={'Are you sure to delete the recipe(s)?'}
          rowData={selectedRecipes}
          columnDef={previewColDefs}
          messageMarginBottom={12}
          modalBodyWidth={956}
        />
      </Page>
    </>
  );
};

export default DeleteRecipes;
